import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { ButtonComponent } from '../../../../../shared/components/ui/button/button.component';
import { ToastService } from '../../../../../shared/services/toast.service';
import { TicketsConfigApiService } from '../../../services/tickets-config-api.service';
import { TicketPriority, TicketSlaTarget } from '../../../models/tickets-config.model';

@Component({
  selector: 'app-tickets-sla-targets-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent],
  templateUrl: './sla-targets-form.component.html'
})
export class TicketsSlaTargetsFormComponent implements OnInit, OnDestroy {
  @Output() saved = new EventEmitter<TicketSlaTarget>();
  @Output() cancelled = new EventEmitter<void>();

  private fb = inject(FormBuilder);
  private configApi = inject(TicketsConfigApiService);
  private toast = inject(ToastService);

  isEditing = false;
  itemId: number | null = null;
  parentPolicyId: number | null = null;
  saving = false;
  error: string | null = null;
  priorities: TicketPriority[] = [];

  form: FormGroup = this.fb.group({
    idTicketPriority: [null, [Validators.required]],
    firstResponseMinutes: [60, [Validators.required, Validators.min(1)]],
    resolutionMinutes: [240, [Validators.required, Validators.min(1)]],
    followUpMinutes: [null, [Validators.min(1)]],
    warningThresholdPct: [25, [Validators.min(0), Validators.max(100)]]
  });

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.configApi.getPriorities()
      .pipe(catchError(() => of([] as TicketPriority[])), takeUntil(this.destroy$))
      .subscribe((p) => { this.priorities = p.sort((a, b) => a.level - b.level); });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  reset(parentPolicyId: number | null = null): void {
    this.isEditing = false;
    this.itemId = null;
    this.parentPolicyId = parentPolicyId;
    this.error = null;
    this.saving = false;
    this.form.reset({
      idTicketPriority: null,
      firstResponseMinutes: 60,
      resolutionMinutes: 240,
      followUpMinutes: null,
      warningThresholdPct: 25
    });
  }

  loadTarget(target: TicketSlaTarget): void {
    this.isEditing = true;
    this.itemId = target.id;
    this.parentPolicyId = target.idSlaPolicy;
    this.form.patchValue({
      idTicketPriority: target.idTicketPriority,
      firstResponseMinutes: target.firstResponseMinutes,
      resolutionMinutes: target.resolutionMinutes,
      followUpMinutes: target.followUpMinutes ?? null,
      warningThresholdPct: target.warningThresholdPct ?? 25
    });
  }

  hasError(field: string): boolean {
    const ctrl = this.form.get(field);
    return !!(ctrl && ctrl.invalid && (ctrl.dirty || ctrl.touched));
  }

  guardar(): void {
    if (this.form.invalid) { this.form.markAllAsTouched(); return; }
    if (!this.isEditing && this.parentPolicyId == null) {
      this.error = 'No se identificó la política padre';
      return;
    }
    this.saving = true;
    this.error = null;
    const raw = this.form.value;
    const followUp = raw.followUpMinutes;
    const req = {
      idTicketPriority: Number(raw.idTicketPriority),
      firstResponseMinutes: Number(raw.firstResponseMinutes),
      resolutionMinutes: Number(raw.resolutionMinutes),
      followUpMinutes: followUp != null && followUp !== '' ? Number(followUp) : null,
      warningThresholdPct: raw.warningThresholdPct != null ? Number(raw.warningThresholdPct) : undefined
    };
    const obs = this.isEditing && this.itemId != null
      ? this.configApi.updateSlaTarget(this.itemId, req)
      : this.configApi.createSlaTarget(this.parentPolicyId!, req);

    obs.pipe(takeUntil(this.destroy$)).subscribe({
      next: (item) => {
        this.saving = false;
        if (this.isEditing) this.toast.updated('Objetivo SLA');
        else this.toast.created('Objetivo SLA');
        this.saved.emit(item);
      },
      error: (err) => {
        this.saving = false;
        const msg = err.error?.message || 'Error al guardar el objetivo SLA';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  cancelar(): void { this.cancelled.emit(); }
}
